// AppUpdateBanner — notifies the user when a new SW version is waiting.
//
// Uses @bbpos/ui Button primitives and the GLASS design tokens so it
// matches the rest of the POS shell (No Borders, Mono Data, Glass Depth).
// The teal→emerald gradient differentiates this from the brand-blue
// InstallPromptBanner so the two never visually blur together.

import { useEffect } from 'react';
import { Button, Spinner } from '@bbpos/ui';
import { useSwStore } from '@/stores/use-sw-store';
import { cn } from '@/constants/design-tokens';

function SyncIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="1 4 1 10 7 10" />
      <polyline points="23 20 23 14 17 14" />
      <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10" />
      <path d="M3.51 15A9 9 0 0 0 18.36 18.36L23 14" />
    </svg>
  );
}

function CloseIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

export function AppUpdateBanner() {
  const needRefresh = useSwStore((s) => s.needRefresh);
  const offlineReady = useSwStore((s) => s.offlineReady);
  const isUpdating = useSwStore((s) => s.isUpdating);
  const dismissed = useSwStore((s) => s.dismissed);
  const updateApp = useSwStore((s) => s.updateApp);
  const dismiss = useSwStore((s) => s.dismiss);

  // Log-only confirmation when the app first becomes offline-ready.
  useEffect(() => {
    if (offlineReady) {
      console.info('[SW] App ready to work offline');
    }
  }, [offlineReady]);

  if (!needRefresh || dismissed) return null;

  return (
    <div
      className={cn(
        'pwa-update-banner',
        'fixed top-0 left-0 right-0 z-50',
        'bg-gradient-to-r from-teal-600/95 via-emerald-600/90 to-success-600/95',
        'backdrop-blur-md text-white',
        'shadow-[0_14px_32px_-20px_rgba(5,150,105,0.7)]',
        'animate-slide-down',
      )}
      role="status"
      aria-live="polite"
    >
      <div className="max-w-6xl mx-auto flex items-center gap-4 px-5 h-12">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/20 shrink-0">
          <SyncIcon className={cn('h-4 w-4 text-emerald-100', isUpdating && 'animate-spin')} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">
            A new version of Orio360 is available
          </p>
          <p className="text-[11px] text-emerald-100/90 leading-tight font-mono tabular-nums">
            Click Update Now to reload with the latest improvements
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => { void updateApp(); }}
          disabled={isUpdating}
          className="!bg-white !text-emerald-700 hover:!bg-emerald-50"
        >
          {isUpdating ? (
            <span className="flex items-center gap-1.5">
              <Spinner size="sm" />
              Updating…
            </span>
          ) : (
            'Update Now'
          )}
        </Button>
        <button
          type="button"
          onClick={dismiss}
          className="p-1.5 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Dismiss update banner"
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  );
}
